import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useStore from '@/stores/useStore'
import useLocalStorageState from 'use-local-storage-state'
import { stopVMJobForIterationFirebaseFunction } from '@/services/Firebase'
import { analyticsTrackEvent, ANALYTIC_EVENTS } from '@/services/Analytics'
import { GUNSLINGER_JOB_STATUSES } from '@/const/const'
import { getIterationComputedStatus, isVMActive } from '@/lib/iteration-utils.ts'
import { MultiSelect } from '@/components/ui/MultipleSelector'

import { Link } from '@/components/catalyst/link'

import { ROUTES } from '@/const/routes'
import { DateTime, Duration } from 'luxon'
import {
  CalendarCheck,
  FlagTriangleRight,
  FolderOpen,
  Monitor,
  MonitorOff,
  MoreHorizontal,
} from 'lucide-react'
import { MProjectPrompt } from '../molecules/MProjectPrompt'
import { cn } from '@/lib/utils.ts'
import { Button as ShadCNButton, Button } from '../ui/button.tsx'
import ALogoSpinner from '../atoms/ALogoSpinner.tsx'
import MIterationStatusPill from '../organisms/project-details/OIterationDetails/MIterationStatusPill.tsx'
import { show as showIntercom } from '@intercom/messenger-js-sdk'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const allowToKillVMForStatus = status => {
  return status === GUNSLINGER_JOB_STATUSES.RUNNING
}

export default function PHome() {
  const projects = useStore(state => state.projects)
  const user = useStore(state => state.user)

  const organizations = useStore(state => state.organizations)
  const isAfterInitialLoad = useStore(state => state.isAfterInitialLoad)
  const getProjectBrokenDownByOrganization = useStore(
    state => state.getProjectBrokenDownByOrganization
  )

  const projectsByOrganization = useMemo(() => {
    if (projects && projects.length > 0 && organizations && organizations.length > 0) {
      return getProjectBrokenDownByOrganization()
    }
    return null
  }, [projects, getProjectBrokenDownByOrganization, organizations])

  const filterOptions = [
    { value: 'my-projects', label: 'My Projects' },
    { value: 'active-vms', label: 'Active Projects' },
  ]
  const [projectFilters, setProjectFilters] = useLocalStorageState('project-filters', {
    defaultValue: [],
    storageSync: true,
  })

  const hasMyProjectsFilter = projectFilters.some(f => f === 'my-projects')
  const hasActiveVMsFilter = projectFilters.some(f => f === 'active-vms')

  if (!isAfterInitialLoad) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/50">
        <ALogoSpinner className="size-8" />
      </div>
    )
  }

  const hasProjects = projectsByOrganization?.some(
    organization => organization.projects && organization.projects.length > 0
  )

  const hasOneOrganization = projectsByOrganization?.length === 1

  return (
    <>
      <RelativeTimeProvider>
        <div className="mb-8 w-[800px]">
          <div className="my-20">
            <MProjectPrompt />
          </div>
          {!hasProjects && <NoProjectsPlaceholder />}
          {!hasOneOrganization && hasProjects && (
            <MultiSelect
              defaultValue={projectFilters}
              onValueChange={setProjectFilters}
              options={filterOptions}
              placeholder="Filter projects..."
            />
          )}
          {!hasProjects && (
            <div className="mt-4">
              <BringYourOwnStack />
            </div>
          )}
          {hasProjects &&
            projectsByOrganization
              .filter(
                organization =>
                  organization.projects.length > 0 &&
                  (hasMyProjectsFilter
                    ? organization.projects.some(project => project.createdBy === user?.id)
                    : true)
              )
              .map((organization, idx) => (
                <div key={organization.id} className="mt-8 space-y-4">
                  <div className="flex w-full gap-1">
                    <div className="font-pp-supply-sans text-3xl font-normal leading-10 text-stone-900">
                      {hasOneOrganization ? 'Projects' : organization.name}
                    </div>
                    {hasOneOrganization && (
                      <div className="ml-auto flex items-center gap-4 place-self-center text-secondary-foreground">
                        <MultiSelect
                          defaultValue={projectFilters}
                          onValueChange={setProjectFilters}
                          options={filterOptions}
                          placeholder="Filter projects..."
                        />
                      </div>
                    )}
                  </div>
                  {idx === 0 && <BringYourOwnStack />}
                  <ProjectList>
                    {organization.projects
                      .filter(project => {
                        const showByUser = hasMyProjectsFilter
                          ? project.createdBy === user?.id
                          : true
                        const showByVM = hasActiveVMsFilter
                          ? isVMActive(project.iterations?.lastIteration?.vmStatus)
                          : true
                        return showByUser && showByVM
                      })
                      .map(project => {
                        const { status, vmStatus, active_role } =
                          project.iterations?.lastIteration ?? {}

                        const lastIterationStatus = getIterationComputedStatus(
                          status,
                          vmStatus,
                          active_role
                        )

                        return (
                          <ProjectItem
                            key={project.id}
                            project={project}
                            iterationStatus={lastIterationStatus}
                          />
                        )
                      })}
                  </ProjectList>
                </div>
              ))}
        </div>
      </RelativeTimeProvider>
    </>
  )
}

function ProjectList({ children }) {
  return <div className="grid w-full grid-cols-[1fr_auto_auto] gap-y-2">{children}</div>
}

function ProjectItem({ project, iterationStatus }) {
  const createdAt = useMemo(() => project.createdAt.toDate(), [project.createdAt])
  const [isWorking, setIsWorking] = useState(false)
  const vmStatus = project.iterations?.lastIteration?.vmStatus

  useEffect(() => {
    if (isWorking && vmStatus === GUNSLINGER_JOB_STATUSES.STOPPED) {
      setIsWorking(false)
    }
  }, [vmStatus, isWorking])

  const stopVM = async e => {
    e.stopPropagation()
    if (!allowToKillVMForStatus(vmStatus)) return

    setIsWorking(true)
    try {
      await stopVMJobForIterationFirebaseFunction({
        iterationId: project.iterations?.lastIteration?.id,
      })
      analyticsTrackEvent(ANALYTIC_EVENTS.ITERATION_STOP_VM, {
        iterationId: project.iterations?.lastIteration?.id,
      })
    } catch (error) {
      console.error('Error stopping VM', error)
      setIsWorking(false)
    }
  }

  return (
    <Link href={ROUTES.PROJECT_DETAILS.replace(':projectId', project.id)} className="contents">
      <div
        className={cn(
          'col-start-1 col-end-4 grid grid-cols-subgrid justify-center gap-6',
          'p-5',
          'rounded-lg',
          'border border-border',
          'bg-card',
          'w-full'
        )}
      >
        <h3
          className={cn(
            'my-auto grow text-sm font-medium tracking-tighter',
            project.name ? 'text-base-foreground' : 'text-muted-foreground'
          )}
        >
          {project.name ?? 'Draft project'}
        </h3>
        <p className="my-auto w-32 text-sm text-muted-foreground lg:w-40">
          Created <Time d={createdAt} />
        </p>
        <div className="min-w-44 place-self-end text-end">
          <div className="flex items-center justify-end gap-2">
            <MIterationStatusPill status={iterationStatus} short />
            {isVMActive(vmStatus) ? (
              <Monitor className="size-4" />
            ) : (
              <MonitorOff className="size-4" />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ShadCNButton variant="ghost" className="size-8 p-0" disabled={isWorking}>
                  <MoreHorizontal className="size-4" />
                </ShadCNButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={!allowToKillVMForStatus(vmStatus) || isWorking}>
                  <div className="flex cursor-pointer items-center" onClick={stopVM}>
                    <FlagTriangleRight strokeWidth={3} className="mr-2 h-6 w-4 text-zinc-600" />
                    <span>{isWorking ? 'Stopping...' : 'Stop project'}</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </Link>
  )
}

/**
 * @param {{d: Date}} props
 */
function Time({ d }) {
  const diff = useRelativeTime(d)
  const { value, scale } = roundToScale(diff)

  const isJustNow = value < 20 && scale === 'seconds'

  return <time title={d.toLocaleString()}>{isJustNow ? 'just now' : rtf.format(value, scale)}</time>
}

/**
 * @param {Duration} dt
 * @returns {{value: number, scale: Intl.RelativeTimeFormatUnit}}
 */
function roundToScale(dt) {
  const units = ['months', 'days', 'hours', 'minutes', 'seconds']
  for (const unit of units) {
    const value = dt.as(unit)
    if (Math.abs(value) >= 1) {
      return { value: Math.round(value), scale: unit }
    }
  }
  return { value: 0, scale: 'seconds' }
}

const rtf = new Intl.RelativeTimeFormat('en', {
  localeMatcher: 'best fit',
  numeric: 'always',
  style: 'narrow',
})

function useRelativeTime(d) {
  const now = useContext(RelativeTimeContext)

  return useMemo(() => {
    return DateTime.fromJSDate(d).diff(now).rescale()
  }, [now, d])
}

const RelativeTimeContext = createContext(null)

const RelativeTimeProvider = ({ children }) => {
  const [now, setNow] = useState(DateTime.now())
  const intervalRef = useRef()

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setNow(DateTime.now())
    }, 10_000)

    return () => clearInterval(intervalRef.current)
  }, [])

  return <RelativeTimeContext.Provider value={now}>{children}</RelativeTimeContext.Provider>
}

function NoProjectsPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-8">
      <FolderOpen className="size-16 rounded-xl border border-border p-4 text-muted-foreground shadow-inner" />
      <h3 className="mt-5 text-base font-medium tracking-tighter text-base-foreground">
        You have no projects yet
      </h3>
      <p className="mt-1 text-sm tracking-tight text-muted-foreground">
        Start your first project above
      </p>
    </div>
  )
}

function BringYourOwnStack() {
  return (
    <div className="flex items-center  rounded-md border border-border p-6 pr-7">
      <div className="text-sm tracking-tight">
        <h3 className="font-medium text-base-foreground">Bring your own tech stack</h3>
        <p className="text-muted-foreground ">
          Create POCs using specific technologies and integrations in minutes.
        </p>
      </div>
      <Button variant="outline" className="ml-auto" onClick={showIntercom}>
        <CalendarCheck /> Talk to Us
      </Button>
    </div>
  )
}
